/**
 * EVENTSFLO-CREW-DOCUMENT-CROP-EDITOR-EDGE-DRAG-AND-SAVE-CROP-FIX-V1
 *
 * Admin-only image editor for crew documents. Replaces the original
 * `react-easy-crop` implementation with `react-image-crop`, which exposes
 * draggable corner + edge handles over a static image — the exact UX
 * admins expect when cleaning up crew-uploaded ID/card photos.
 *
 * Coordinate contract
 * -------------------
 * Order of operations (frontend visual preview + backend Pillow):
 *   1. Rotate the source image by `rotation` degrees (with expand=True).
 *   2. Crop the rotated image at `{x, y, width, height}` *in natural pixels
 *      of the rotated image*.
 *
 * The frontend pre-rotates the source onto a `<canvas>` at natural size and
 * uses the rotated bitmap as the source for the crop handles. As a result
 * the `crop` payload sent to the backend is always in rotated-natural
 * pixel space — identical to what Pillow operates on server-side.
 *
 * If the admin never drags the crop, the default crop covers the whole
 * rotated image — the save is then equivalent to rotate-only.
 *
 * EVENTSFLO-CREW-DOCUMENT-CROP-EDITOR-IMAGE-NOT-DISPLAYING-FIX-V1
 * The source image is still fetched through the authenticated `api` client
 * as a blob and wrapped in `URL.createObjectURL()`; bare <img src> on the
 * protected download endpoint cannot send Bearer headers.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Loader2, RotateCw, RotateCcw, Crop as CropIcon, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/** Build a centered 90%×90% crop in displayed CSS pixels. */
function makeInitialCrop(displayedW, displayedH) {
  return centerCrop(
    makeAspectCrop({ unit: "%", width: 90 }, displayedW / displayedH, displayedW, displayedH),
    displayedW,
    displayedH,
  );
}

export function DocumentImageEditorModal({
  open,
  onOpenChange,
  document,
  staffId,
  onSaveComplete,
}) {
  const [rotation, setRotation] = useState(0);
  const [crop, setCrop] = useState(undefined);            // PercentCrop or PixelCrop
  const [completedCrop, setCompletedCrop] = useState(null); // PixelCrop in displayed CSS px
  const [saving, setSaving] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [sourceObjectUrl, setSourceObjectUrl] = useState(null);   // blob: of source
  const [rotatedObjectUrl, setRotatedObjectUrl] = useState(null); // blob: of rotated canvas

  const imgRef = useRef(null);                  // <img> shown in cropper
  const sourceUrlRef = useRef(null);
  const rotatedUrlRef = useRef(null);
  const sourceBitmapRef = useRef(null);         // HTMLImageElement of source for redraws

  const revokeUrl = (refObj) => {
    if (refObj.current) {
      try { URL.revokeObjectURL(refObj.current); } catch (_) { /* noop */ }
      refObj.current = null;
    }
  };

  // ---- Lifecycle: reset state on open / doc switch ------------------------
  useEffect(() => {
    if (open) {
      setRotation(0);
      setCrop(undefined);
      setCompletedCrop(null);
      setImageError(false);
    }
  }, [open, document?.id]);

  // ---- Authenticated blob fetch of the source image -----------------------
  useEffect(() => {
    if (!open || !document?.id || !staffId) {
      revokeUrl(sourceUrlRef);
      revokeUrl(rotatedUrlRef);
      sourceBitmapRef.current = null;
      setSourceObjectUrl(null);
      setRotatedObjectUrl(null);
      setImageLoading(false);
      return;
    }
    let cancelled = false;
    setImageLoading(true);
    setImageError(false);
    (async () => {
      try {
        const { data } = await api.get(
          `/staff/${staffId}/documents/${document.id}/download`,
          { responseType: "blob" },
        );
        if (cancelled) return;
        revokeUrl(sourceUrlRef);
        const url = URL.createObjectURL(data);
        sourceUrlRef.current = url;
        setSourceObjectUrl(url);

        // Pre-decode into an HTMLImageElement so we can re-render the
        // rotated canvas without re-fetching the blob.
        const bitmap = new Image();
        bitmap.onload = () => {
          if (cancelled) return;
          sourceBitmapRef.current = bitmap;
          // First paint: rotation=0 → use source directly.
          setRotatedObjectUrl(url);
          setImageLoading(false);
        };
        bitmap.onerror = () => {
          if (cancelled) return;
          setImageError(true);
          setImageLoading(false);
        };
        bitmap.src = url;
      } catch (_err) {
        if (!cancelled) {
          setImageError(true);
          setImageLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, document?.id, staffId]);

  // ---- Re-render rotated canvas whenever rotation changes ----------------
  useEffect(() => {
    const bitmap = sourceBitmapRef.current;
    if (!bitmap) return;
    if (rotation === 0) {
      // Reuse the source blob URL for rotation 0.
      revokeUrl(rotatedUrlRef);
      setRotatedObjectUrl(sourceUrlRef.current);
      // Reset crop because container dims changed.
      setCrop(undefined);
      setCompletedCrop(null);
      return;
    }
    const w = bitmap.naturalWidth;
    const h = bitmap.naturalHeight;
    const swap = rotation % 180 !== 0;
    const canvas = window.document.createElement("canvas");
    canvas.width = swap ? h : w;
    canvas.height = swap ? w : h;
    const ctx = canvas.getContext("2d");
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.drawImage(bitmap, -w / 2, -h / 2);
    canvas.toBlob((blob) => {
      if (!blob) return;
      revokeUrl(rotatedUrlRef);
      const url = URL.createObjectURL(blob);
      rotatedUrlRef.current = url;
      setRotatedObjectUrl(url);
      // Reset crop on rotation since visible canvas dims changed.
      setCrop(undefined);
      setCompletedCrop(null);
    }, "image/png");
  }, [rotation]);

  // ---- Final unmount cleanup ---------------------------------------------
  useEffect(() => () => {
    revokeUrl(sourceUrlRef);
    revokeUrl(rotatedUrlRef);
  }, []);

  // Initialise default crop when the rotated image first lands.
  const onImageLoad = useCallback((e) => {
    const { width, height } = e.currentTarget;
    setCrop(makeInitialCrop(width, height));
  }, []);

  if (!document) return null;

  const handleRotate = (delta) => {
    setRotation((r) => (((r + delta) % 360) + 360) % 360);
  };
  const handleReset = () => {
    setRotation(0);
    setCrop(undefined);
    setCompletedCrop(null);
  };

  const handleSave = async () => {
    if (saving) return;
    const img = imgRef.current;
    if (!img) {
      toast.error("Image not ready yet");
      return;
    }
    // ReactCrop gives crop in CSS pixels of the displayed <img>. Convert to
    // *natural* pixels of the rotated bitmap.
    const scaleX = img.naturalWidth / img.width;
    const scaleY = img.naturalHeight / img.height;

    let pixelCrop = null;
    if (completedCrop && completedCrop.width > 0 && completedCrop.height > 0) {
      const cw = Math.round(completedCrop.width * scaleX);
      const ch = Math.round(completedCrop.height * scaleY);
      if (cw < 5 || ch < 5) {
        toast.error("Crop area is too small");
        return;
      }
      pixelCrop = {
        x: Math.max(0, Math.round(completedCrop.x * scaleX)),
        y: Math.max(0, Math.round(completedCrop.y * scaleY)),
        width: cw,
        height: ch,
      };
    }

    setSaving(true);
    try {
      const { data } = await api.post(
        `/staff/${staffId}/documents/${document.id}/image-edit`,
        { rotation, crop: pixelCrop },
      );
      toast.success("Cleaned image saved as a new version");
      onSaveComplete?.(data);
      onOpenChange?.(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Save failed";
      toast.error(typeof msg === "string" ? msg : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !saving) onOpenChange?.(false); }}>
      <DialogContent className="max-w-3xl" data-testid="document-image-editor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CropIcon className="h-4 w-4" /> Crop &amp; rotate image
          </DialogTitle>
          <DialogDescription className="text-xs">
            Drag the crop rectangle edges and corners to keep only the
            document, then save. A new cleaned version is created — the
            original upload remains in version history.
          </DialogDescription>
        </DialogHeader>

        <div
          className="relative w-full bg-muted/40 rounded-md overflow-auto flex items-center justify-center"
          style={{ minHeight: 420, maxHeight: 520 }}
          data-testid="document-image-editor-canvas"
        >
          {imageLoading && !imageError && (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-sm text-muted-foreground"
              data-testid="document-image-editor-loading"
            >
              <Loader2 className="h-5 w-5 animate-spin" /> Loading image…
            </div>
          )}
          {imageError ? (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-sm text-muted-foreground px-6 text-center"
              data-testid="document-image-editor-error"
            >
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Unable to load image for editing. Try downloading the file or
              refreshing the page.
            </div>
          ) : rotatedObjectUrl ? (
            <ReactCrop
              crop={crop}
              onChange={(_pixel, percent) => setCrop(percent)}
              onComplete={(pixel) => setCompletedCrop(pixel)}
              keepSelection
              ruleOfThirds
              data-testid="document-image-editor-react-crop"
            >
              <img
                ref={imgRef}
                src={rotatedObjectUrl}
                alt="Document being edited"
                onLoad={onImageLoad}
                style={{ maxHeight: 500, maxWidth: "100%", display: "block" }}
                data-testid="document-image-editor-img"
              />
            </ReactCrop>
          ) : null}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
          <div className="space-y-1 flex flex-col">
            <Label className="text-xs">Rotation</Label>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleRotate(-90)}
                disabled={saving || imageLoading}
                data-testid="document-image-editor-rotate-left"
              >
                <RotateCcw className="h-4 w-4 mr-1" />-90°
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleRotate(90)}
                disabled={saving || imageLoading}
                data-testid="document-image-editor-rotate-right"
              >
                <RotateCw className="h-4 w-4 mr-1" />+90°
              </Button>
              <span
                className="text-xs text-muted-foreground ml-1"
                data-testid="document-image-editor-rotation-value"
              >
                {rotation}°
              </span>
            </div>
          </div>
          <div className="flex items-end justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleReset}
              disabled={saving}
              data-testid="document-image-editor-reset"
            >
              <X className="h-3.5 w-3.5 mr-1" /> Reset
            </Button>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange?.(false)}
            disabled={saving}
            data-testid="document-image-editor-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || imageError || imageLoading || !rotatedObjectUrl}
            data-testid="document-image-editor-save-btn"
          >
            {saving ? (
              <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</>
            ) : (
              <><CropIcon className="h-4 w-4 mr-1" /> Save cleaned version</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DocumentImageEditorModal;
